import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  Home, 
  Building, 
  Mail, 
  FileText, 
  AlertTriangle, 
  Users, 
  Settings,
  Menu,
  X,
  LogOut,
  ChevronDown,
  BarChart3,
  Ticket,
  Database,
  Shield,
  Globe,
  Mic,
  CreditCard,
  FileSignature,
  Cpu,
  Star,
  GraduationCap,
  Rocket,
  Webhook,
  Radio,
  Award,
  Target,
  MessageSquare,
  Tag,
  DollarSign
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import OnboardingVideoPopup from '../ui/OnboardingVideoPopup';

const DashboardLayout = () => {
  const { user, logout, hasPermission, permissions } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      permission: null
    },
    {
      name: 'Estimates',
      href: '/dashboard/estimates',
      icon: FileText,
      permission: null
    },
    {
      name: 'Insurance Claims',
      href: '/dashboard/claims',
      icon: Shield,
      permission: null
    },
    {
      name: 'FieldCam AI',
      href: '/dashboard/field-cam',
      icon: Mic,
      permission: null,
      badge: 'NEW'
    },
    {
      name: 'Field Reports',
      href: '/dashboard/field-reports',
      icon: FileText,
      permission: null
    },
    {
      name: 'Email Funnel',
      href: '/dashboard/email-funnel',
      icon: Mail,
      permission: null,
      badge: 'NEW'
    },
    {
      name: 'Broadcast',
      href: '/dashboard/broadcast',
      icon: Radio,
      permission: null,
      badge: 'NEW'
    },
    {
      name: 'Lead Generation',
      href: '/dashboard/leadgen',
      icon: Globe,
      permission: null
    },
    {
      name: 'Lead Pipeline',
      href: '/dashboard/crm',
      icon: Users,
      permission: null,
      badge: 'NEW'
    },
    {
      name: 'Lead Sources',
      href: '/dashboard/lead-sources',
      icon: Tag,
      permission: 'users.read'
    },
    {
      name: 'Webhooks',
      href: '/dashboard/webhooks',
      icon: Webhook,
      permission: null,
      badge: 'NEW'
    },
    {
      name: 'Email Management',
      href: '/dashboard/email-management',
      icon: Mail,
      permission: null
    },
    {
      name: 'Team Members',
      href: '/dashboard/team',
      icon: Users,
      permission: null
    },
    {
      name: 'My Rates',
      href: '/dashboard/my-rates',
      icon: BarChart3,
      permission: null
    },
    {
      name: 'Rate Cards',
      href: '/dashboard/rate-cards',
      icon: DollarSign,
      permission: null
    },
    {
      name: 'Payment Setup',
      href: '/dashboard/stripe',
      icon: CreditCard,
      permission: null
    },
    {
      name: 'Contract Templates',
      href: '/dashboard/contracts',
      icon: FileSignature,
      permission: null
    },
    {
      name: 'Reviews & Referrals',
      href: '/dashboard/reviews',
      icon: Star,
      permission: null,
      badge: 'NEW'
    },
    {
      name: 'Add-on Services',
      href: '/dashboard/addons',
      icon: Rocket,
      permission: null,
      badge: 'PRO'
    },
    {
      name: 'Help & Tutorials',
      href: '/tutorial',
      icon: GraduationCap,
      permission: null,
      external: true
    }
  ];

  // Admin navigation items
  // Check if user is super admin
  const isSuperAdmin = user?.role === 'super_admin' || user?.email === 'admin@homefixer360.com';

  const adminNavigation = [
    {
      name: 'Lead Applications',
      href: '/dashboard/contractor-applications',
      icon: Award,
      permission: 'users.manage',
      badge: 'Admin'
    },
    {
      name: 'System Users',
      href: '/dashboard/admin/users',
      icon: Shield,
      permission: 'users.manage',
      badge: 'Admin'
    },
    {
      name: 'Coupon Codes',
      href: '/dashboard/admin/coupons',
      icon: Ticket,
      permission: 'coupons.manage',
      badge: 'Admin'
    },
    {
      name: 'CRM Dashboard',
      href: '/dashboard/admin/crm',
      icon: Database,
      permission: 'users.manage',
      badge: 'Admin'
    },
    // Only show AI Costs and Changelog to super admin
    ...(isSuperAdmin ? [
      {
        name: 'Platform Sales',
        href: '/dashboard/admin/platform-sales',
        icon: Target,
        permission: 'users.manage',
        badge: 'Super Admin'
      },
      {
        name: 'AI & Platform Costs',
        href: '/dashboard/admin/ai-costs',
        icon: Cpu,
        permission: 'users.manage',
        badge: 'Super Admin'
      },
      {
        name: 'Changelog',
        href: '/dashboard/admin/changelog',
        icon: FileSignature,
        permission: 'users.manage',
        badge: 'Super Admin'
      }
    ] : [])
  ];

  // Settings navigation items
  const settingsNavigation = [
    {
      name: 'Account',
      href: '/dashboard/account',
      icon: Settings,
      permission: null
    },
    {
      name: 'Messaging',
      href: '/dashboard/messaging-settings',
      icon: MessageSquare,
      permission: null
    },
    {
      name: 'Billing',
      href: '/dashboard/billing',
      icon: BarChart3,
      permission: null
    }
  ];

  const filteredNavigation = navigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const filteredAdminNavigation = adminNavigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const filteredSettingsNavigation = settingsNavigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <Link to="/dashboard" className="flex items-center">
              <div className="text-xl font-bold text-blue-600">HomeFixer360</div>
              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Pro</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {/* Main Navigation */}
            <div className="space-y-1">
              {filteredNavigation.map((item) => {
                const Icon = item.icon;
                // For external links (outside dashboard), use anchor tag with full page navigation
                if (item.external) {
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                      {item.badge && (
                        <span className="ml-auto bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </a>
                  );
                }
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive(item.href)
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                    {item.badge && (
                      <span className="ml-auto bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Admin Section */}
            {filteredAdminNavigation.length > 0 && (
              <div className="pt-6">
                <div className="flex items-center px-4 mb-2">
                  <Shield className="w-4 h-4 mr-2 text-gray-400" />
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Admin
                  </h3>
                </div>
                <div className="space-y-1">
                  {filteredAdminNavigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          isActive(item.href)
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-5 h-5 mr-3" />
                        {item.name}
                        {item.badge && (
                          <span className="ml-auto bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-semibold">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Settings Section */}
            <div className="pt-6">
              <div className="flex items-center px-4 mb-2">
                <Settings className="w-4 h-4 mr-2 text-gray-400" />
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Settings
                </h3>
              </div>
              <div className="space-y-1">
                {filteredSettingsNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive(item.href)
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* User Menu */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-700">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            
            <div className="space-y-1">
              <Link
                to="/dashboard/settings"
                className="flex items-center px-3 py-2 text-sm text-gray-600 rounded-md hover:bg-gray-100"
              >
                <Settings className="w-4 h-4 mr-3" />
                Settings
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-600 rounded-md hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4 mr-3" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6 text-gray-500" />
            </button>

            <div className="flex items-center space-x-4 ml-auto">
              {/* Quick Actions */}
              <div className="hidden md:flex items-center space-x-2">
                <Link to="/">
                  <Button variant="outline" size="sm">
                    View Landing Page
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <main className="min-h-screen bg-gray-50">
          <Outlet />
        </main>
        
        {/* Onboarding Video Popup for First-Time Users */}
        <OnboardingVideoPopup />
      </div>
    </div>
  );
};

export default DashboardLayout;